import { type ReactNode, useMemo, useState } from 'react';
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown, Search } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Button } from './button';
import { Input } from './input';
import { Skeleton } from './skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table';

/**
 * Sortable / searchable / paginated table, replacing `Table` from
 * `@backstage/core-components` (material-table) per ADR-0012.
 *
 * The column shape deliberately mirrors Backstage's `TableColumn` — `title`,
 * `field`, `render` — so migrating a call site is a rename rather than a
 * rewrite. `sortValue` replaces `customSort`: returning a comparable key is
 * enough for every ordering the portal needs, and keeps sorting declarative.
 */
export interface DataTableColumn<T> {
  title: string;
  /** Key used for the default cell value, sorting and search. */
  field: Extract<keyof T, string>;
  /** Custom cell renderer; falls back to the raw `field` value. */
  render?: (row: T) => ReactNode;
  /** Projects the row onto a comparable key for non-lexical ordering. */
  sortValue?: (row: T) => string | number;
  sortable?: boolean;
  width?: string;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  title?: ReactNode;
  isLoading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  pageSize?: number;
  initialSort?: { field: Extract<keyof T, string>; desc?: boolean };
  emptyContent?: ReactNode;
  className?: string;
}

const SKELETON_ROWS = 5;
const SKELETON_CELL_WIDTHS = ['w-3/4', 'w-1/2', 'w-2/3', 'w-5/12', 'w-7/12'];

const SortIcon = ({ direction }: { direction: false | 'asc' | 'desc' }) => {
  if (direction === 'asc') return <ArrowUp className="h-3 w-3" />;
  if (direction === 'desc') return <ArrowDown className="h-3 w-3" />;
  return <ChevronsUpDown className="h-3 w-3 opacity-40" />;
};

export function DataTable<T extends object>({
  columns,
  data,
  title,
  isLoading = false,
  searchable = true,
  searchPlaceholder = 'Search',
  pageSize = 20,
  initialSort,
  emptyContent,
  className,
}: Readonly<DataTableProps<T>>) {
  const [sorting, setSorting] = useState<SortingState>(
    initialSort ? [{ id: initialSort.field, desc: initialSort.desc ?? false }] : [],
  );
  const [globalFilter, setGlobalFilter] = useState('');

  const tableColumns = useMemo<ColumnDef<T>[]>(
    () =>
      columns.map(col => ({
        id: col.field,
        accessorFn: (row: T) =>
          col.sortValue ? col.sortValue(row) : (row[col.field] ?? ''),
        header: col.title,
        enableSorting: col.sortable ?? true,
        meta: { width: col.width },
        cell: ({ row }) =>
          col.render ? col.render(row.original) : String(row.original[col.field] ?? ''),
      })),
    [columns],
  );

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  });

  const rows = table.getRowModel().rows;

  let body;
  if (isLoading) {
    // One cell per real column, each a text-sm line (h-4 + my-0.5 = the 20px
    // line height) at a width that varies by row and column, so the loaded
    // rows drop in without the table jumping.
    body = Array.from({ length: Math.min(pageSize, SKELETON_ROWS) }).map(
      (_, rowIndex) => (
        // eslint-disable-next-line react/no-array-index-key
        <TableRow key={`skeleton-${rowIndex}`} aria-hidden="true">
          {columns.map((col, colIndex) => (
            <TableCell key={col.field}>
              <Skeleton
                className={cn(
                  'my-0.5 h-4',
                  SKELETON_CELL_WIDTHS[
                    (rowIndex + colIndex * 2) % SKELETON_CELL_WIDTHS.length
                  ],
                )}
              />
            </TableCell>
          ))}
        </TableRow>
      ),
    );
  } else if (rows.length === 0) {
    body = (
      <TableRow>
        <TableCell colSpan={columns.length} className="p-0">
          {emptyContent ?? (
            <p className="p-8 text-center text-sm text-muted-foreground">
              No results
            </p>
          )}
        </TableCell>
      </TableRow>
    );
  } else {
    body = rows.map(row => (
      <TableRow key={row.id}>
        {row.getVisibleCells().map(cell => (
          <TableCell key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ));
  }

  return (
    <div className={cn('rounded-lg border border-border bg-card', className)}>
      {(title || searchable) && (
        <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
          {title && (
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          )}
          {searchable && (
            <div className="relative sm:w-64">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={globalFilter}
                onChange={event => setGlobalFilter(event.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="pl-8"
              />
            </div>
          )}
        </div>
      )}

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();
                const width = (header.column.columnDef.meta as { width?: string } | undefined)?.width;
                return (
                  <TableHead key={header.id} style={width ? { width } : undefined}>
                    {canSort ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className="inline-flex items-center gap-1 hover:text-foreground"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <SortIcon direction={sorted} />
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>{body}</TableBody>
      </Table>

      {table.getPageCount() > 1 && (
        <div className="flex items-center justify-between border-t border-border p-3 text-sm text-muted-foreground">
          <span>
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
